import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Stack,
  TableRow,
  TableBody,
  TableCell,
  Container,
  TableContainer,
  Grid,
  Typography,
  Button,
  TablePagination,
  Checkbox,
  colors,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box
} from '@mui/material';
import Page from 'src/components/Page';
import Scrollbar from 'src/components/Scrollbar';
import SearchNotFound from 'src/components/SearchNotFound';
import CircularProgress from '@mui/material/CircularProgress';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaCheck } from 'react-icons/fa';
import { IoIosCheckmark } from 'react-icons/io';
import palette from 'src/theme/palette';
import { CiSearch } from 'react-icons/ci';
import './../../styles/users.css';
import 'react-toastify/dist/ReactToastify.css';
import { useQuery } from '@tanstack/react-query';
import { useQueryClient } from '@tanstack/react-query';
import { studentService } from 'src/services/student.service';
import { classService } from 'src/services/class.service';
import { Select as CustomSelect } from 'src/components';
import { UserListHead } from 'src/sections/@dashboard/user';

const TABLE_HEAD = [
  { id: 'StudentName', label: 'Student Name', alignRight: false },
  { id: '', label: '', alignRight: false },
  { id: 'Email', label: 'Email', alignRight: false },
  { id: 'Student ID', label: 'Student ID', alignRight: false },
  { id: 'Grade', label: 'Grade', alignRight: false },
];

const gradeOptions = [
  { value: '1', label: '1' },
  { value: '2', label: '2' },
  { value: '3', label: '3' },
  { value: '4', label: '4' },
  { value: '5', label: '5' },
  { value: '6', label: '6' },
  { value: '7', label: '7' },
  { value: '8', label: '8' },
  { value: '9', label: '9' },
  { value: '10', label: '10' },
  { value: '11', label: '11' },
  { value: '12', label: '12' },
];

export default function AddStudents() {
  const navigate = useNavigate();
  const { id: classId } = useParams<{ id: string }>();
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchText, setSearchText] = useState('');
  const [selectedStudents, setSelectedStudents] = useState<string[]>([]);
  const [originallyEnrolled, setOriginallyEnrolled] = useState<string[]>([]);
  const [selectedGrade, setSelectedGrade] = useState<string>('');

  const queryClient = useQueryClient();

  // Query for students with pagination
  const {
    data: studentData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['students', { page, rowsPerPage, searchText, grade: selectedGrade, classId }],
    queryFn: () =>
      studentService.getStudents({
        page,
        limit: rowsPerPage,
        search: searchText,
        grade: selectedGrade,
        classId,
        includeEnrolled: true
      }),
    staleTime: 0, 
    refetchOnMount: true, 
    refetchOnWindowFocus: true, 
  });

  // Pre-select enrolled students and track original enrollment
  useEffect(() => {
    if (studentData?.data) {
      const enrolledStudents = studentData.data
        .filter(student => student.isEnrolled)
        .map(student => student.id);

      // Update originally enrolled students
      setOriginallyEnrolled(prev => [...new Set([...prev, ...enrolledStudents])]);
      
      setSelectedStudents(prevSelected => {
        // Keep existing selections that are still in the current page
        const currentIds = studentData.data.map(s => s.id);
        const existingSelections = prevSelected.filter(id => currentIds.includes(id));
        // Add newly found enrolled students
        return [...new Set([...existingSelections, ...enrolledStudents])];
      });
    }
  }, [studentData?.data]);

  const handleSaveChanges = async () => {
    try {
      // Determine which students to enroll and unenroll
      const studentsToEnroll = selectedStudents.filter(id => !originallyEnrolled.includes(id));
      const studentsToUnenroll = originallyEnrolled.filter(id => !selectedStudents.includes(id));

      // Process enrollments and unenrollments
      const promises = [];
      if (studentsToEnroll.length > 0) {
        promises.push(classService.enrollStudentsInClass(studentsToEnroll, classId));
      }
      if (studentsToUnenroll.length > 0) {
        promises.push(classService.unenrollStudentsFromClass(studentsToUnenroll, classId));
      }

      await Promise.all(promises);
      
      toast.success(
        studentsToEnroll.length && studentsToUnenroll.length
          ? 'Students enrolled and unenrolled successfully'
          : studentsToEnroll.length
          ? 'Students enrolled successfully'
          : 'Students unenrolled successfully'
      );
      
      // Invalidate both students and classes queries
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['students'] }),
        queryClient.invalidateQueries({ queryKey: ['classes'] }),
        // Force refetch the current query
        queryClient.refetchQueries({ 
          queryKey: ['students', { page, rowsPerPage, searchText, grade: selectedGrade, classId }],
          exact: true 
        })
      ]);

      navigate(-1);
    } catch (error) {
      console.error('Failed to update student enrollments:', error);
      toast.error('Failed to update student enrollments');
    }
  };

  // Handlers
  const handleCheckboxChange = (id: string) => {
    setSelectedStudents((prevSelected) =>
      prevSelected.includes(id) ? prevSelected.filter((studentId) => studentId !== id) : [...prevSelected, id]
    );
  };

  const handleGradeChange = (event: any) => {
    setSelectedGrade(event.target.value);
    setPage(1); // Reset to first page when filter changes
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value);
    setPage(1); // Reset to first page when search changes
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    setPage(newPage + 1); // Convert from 0-based to 1-based pagination
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(1); // Reset to first page when rows per page changes
  };

  // Early return for error state
  if (error) {
    return (
      <Container>
        <Typography color="error">Error loading students. Please try again later.</Typography>
      </Container>
    );
  }

  console.log(studentData)

  return (
    <Page title="Add Students">
      <Container maxWidth={false}>
        <Card>
          <Grid container spacing={2} sx={{ py: 3, px: 3 }}>
            <Grid item sm={12} xs={12} md={6} lg={6} xl={6}>
              <div className="_search_field_wrapper">
                <input
                  type="text"
                  placeholder="Search by Name, Email or ID"
                  className="_auth_fields searchField"
                  value={searchText}
                  onChange={handleSearchChange}
                />
              </div>
            </Grid>
            <Grid item sm={12} xs={12} md={6} lg={6} xl={6}>
              <FormControl fullWidth>
                <InputLabel id="grade-select-label">Grade</InputLabel>
                <Select
                  labelId="grade-select-label"
                  id="grade-select"
                  value={selectedGrade}
                  label="Grade"
                  onChange={handleGradeChange}
                >
                  <MenuItem value="">All Grades</MenuItem>
                  {gradeOptions.map((grade) => (
                    <MenuItem key={grade.value} value={grade.value}>
                      Grade {grade.value}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Scrollbar>
            <TableContainer>
              {isLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <Table>
                  <UserListHead headLabel={TABLE_HEAD} rowCount={studentData?.total || 0} />
                  <TableBody>
                    {studentData?.data?.map((student) => {
                      const { id, badge, grade, user } = student;
                      const isSelected = selectedStudents.includes(id);
                      return (
                        <TableRow hover key={id}>
                          <TableCell scope="row">
                            <Stack direction="row" alignItems="center" spacing={2}>
                              <div
                                className="checkBox"
                                onClick={() => handleCheckboxChange(id)}
                                style={{ backgroundColor: isSelected ? palette.primary.main : '' }}
                              >
                                {isSelected && <FaCheck color={palette.common.white} size={12} />}
                              </div>
                              <Typography color="#111827" variant="subtitle1">
                                {user?.firstname} {user?.lastname}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell>
                            {badge && (
                              <img
                                src={badge?.imageUrl}
                                alt={badge?.name}
                                style={{ width: 40, height: 40, borderRadius: '50%' }}
                              />
                            )}
                          </TableCell>
                          <TableCell align="left">{user?.email}</TableCell>
                          <TableCell align="left">{user?.extendedProperties?.studentId || '--'}</TableCell>
                          <TableCell align="left">{grade || '--'}</TableCell>
                        </TableRow>
                      );
                    })}

                    {!isLoading && (!studentData?.data || studentData.data.length === 0) && (
                      <TableRow>
                        <TableCell align="center" colSpan={6}>
                          <SearchNotFound searchQuery={searchText} />
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              )}
            </TableContainer>

            <TablePagination
              component="div"
              count={studentData?.total || 0}
              page={(page - 1)} // Convert 1-based to 0-based for MUI
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={[10, 25, 50]}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Scrollbar>

          <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button
              variant="outlined"
              onClick={() => navigate(-1)}
              sx={{ height: 48, width: 120 }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSaveChanges}
              sx={{ height: 48, width: 120 }}
            >
              Save
            </Button>
          </Box>
        </Card>
      </Container>
    </Page>
  );
}
